import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DialogBackdrop,
  DialogClose,
  DialogDescription,
  DialogPopup,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  dialogBackdropClass,
  dialogPopupClass,
} from "./Dialog.js";

export type ConfirmationTone = "default" | "warning" | "danger";

export type ConfirmationRequest = {
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmationTone;
};

type PendingConfirmation = ConfirmationRequest & {
  id: number;
  resolve: (value: boolean) => void;
};

type ConfirmFn = (request: ConfirmationRequest) => Promise<boolean>;

const ConfirmationContext = createContext<ConfirmFn | null>(null);

export function useConfirmation(): ConfirmFn {
  const confirm = useContext(ConfirmationContext);
  if (!confirm) {
    throw new Error("useConfirmation must be used within a ConfirmationProvider");
  }
  return confirm;
}

const actionBaseClass =
  "rounded-[var(--radius-control)] border px-3 py-1.5 text-xs font-medium transition-colors outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50";

const cancelActionClass = [
  actionBaseClass,
  "border-hairline bg-panel-hi text-ink hover:border-signal focus-visible:outline-signal",
].join(" ");

const toneConfirmClass: Record<ConfirmationTone, string> = {
  default: [
    actionBaseClass,
    "border-transparent bg-[var(--signal-surface)] text-[var(--signal-on)] hover:bg-[var(--signal-surface-hover)] focus-visible:outline-signal",
  ].join(" "),
  warning: [
    actionBaseClass,
    "border-transparent bg-[var(--warn-surface)] text-[var(--warn-on)] hover:bg-[var(--warn-surface-hover)] focus-visible:outline-warn",
  ].join(" "),
  danger: [
    actionBaseClass,
    "border-transparent bg-[var(--danger-surface)] text-[var(--danger-on)] hover:bg-[var(--danger-surface-hover)] focus-visible:outline-danger",
  ].join(" "),
};

const confirmationPopupClass = [
  dialogPopupClass,
  "flex flex-col gap-3",
  "p-4",
  "pt-[max(1rem,var(--safe-top,0px))]",
  "pb-[max(1rem,var(--safe-bottom,0px))]",
  "max-h-[calc(100dvh-2rem-var(--safe-top,0px)-var(--safe-bottom,0px))] overflow-y-auto",
].join(" ");

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<PendingConfirmation[]>([]);
  const [open, setOpen] = useState(false);
  const [displayed, setDisplayed] = useState<PendingConfirmation | null>(null);
  const nextIdRef = useRef(0);
  const settledRef = useRef<Set<number>>(new Set());
  const cancelRef = useRef<HTMLButtonElement>(null);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  const settle = useCallback((id: number, value: boolean) => {
    if (settledRef.current.has(id)) return;
    settledRef.current.add(id);

    setQueue((prev) => {
      const head = prev[0];
      if (!head || head.id !== id) return prev;
      queueMicrotask(() => head.resolve(value));
      return prev.slice(1);
    });
  }, []);

  const confirm = useCallback((request: ConfirmationRequest): Promise<boolean> => {
    return new Promise((resolve) => {
      const id = ++nextIdRef.current;
      setQueue((prev) => [...prev, { ...request, id, resolve }]);
    });
  }, []);

  useEffect(() => {
    if (queue.length > 0 && !open && displayed === null) {
      setDisplayed(queue[0] ?? null);
      setOpen(true);
    }
  }, [queue, open, displayed]);

  useEffect(() => {
    return () => {
      for (const item of queueRef.current) {
        if (!settledRef.current.has(item.id)) {
          settledRef.current.add(item.id);
          item.resolve(false);
        }
      }
    };
  }, []);

  function closeDisplayed(value: boolean) {
    if (!displayed) return;
    settle(displayed.id, value);
    setOpen(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) return;
    if (displayed && !settledRef.current.has(displayed.id)) {
      settle(displayed.id, false);
    }
    setOpen(false);
  }

  function handleOpenChangeComplete(nextOpen: boolean) {
    if (nextOpen) return;
    setDisplayed(null);
    const next = queueRef.current[0];
    if (next) {
      setDisplayed(next);
      setOpen(true);
    }
  }

  const tone = displayed?.tone ?? "default";
  const cancelLabel = displayed?.cancelLabel ?? "Cancel";

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      <DialogRoot
        open={open}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={handleOpenChangeComplete}
        modal
      >
        <DialogPortal>
          <DialogBackdrop className={dialogBackdropClass} data-confirmation-backdrop="" />
          {displayed ? (
            <DialogPopup
              className={confirmationPopupClass}
              initialFocus={cancelRef}
              data-tone={tone}
            >
              <div className="flex items-start justify-between gap-3">
                <DialogTitle className="min-w-0 flex-1 text-sm font-medium tracking-wide text-ink">
                  {displayed.title}
                </DialogTitle>
                <DialogClose
                  aria-label="Close"
                  className="shrink-0 rounded-[var(--radius-control)] border border-hairline bg-panel-hi px-2 py-1 text-xs text-ink-muted hover:border-signal hover:text-ink"
                >
                  ×
                </DialogClose>
              </div>
              <DialogDescription
                render={
                  <div className="min-w-0 text-xs leading-relaxed text-ink-muted [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:text-ink [&_li]:break-words" />
                }
              >
                {displayed.description}
              </DialogDescription>
              <div className="mt-1 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  ref={cancelRef}
                  type="button"
                  className={cancelActionClass}
                  onClick={() => closeDisplayed(false)}
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  className={toneConfirmClass[tone]}
                  data-confirmation-action="confirm"
                  onClick={() => closeDisplayed(true)}
                >
                  {displayed.confirmLabel}
                </button>
              </div>
            </DialogPopup>
          ) : null}
        </DialogPortal>
      </DialogRoot>
    </ConfirmationContext.Provider>
  );
}
